import React, { useRef, useState, useEffect, useCallback, createContext, useContext, useMemo } from 'react';

const keyEventTriggerClick = function (e, keyCode) {
  if (keyCode === void 0) {
    keyCode = ['Space', 'Enter'];
  }
  if (keyCode.find(value => e.code === value)) {
    e.preventDefault();
    e.target.click();
  }
};

class MicroApp {
  constructor(appId, microAppExtensionId) {
    if (microAppExtensionId === void 0) {
      microAppExtensionId = 'hikhjjepnpajjkjbboiohgemgockemef';
    }
    this.appId = null;
    this.config = null;
    this.subscription = null;
    this.microAppExtensionId = null;
    assert(!!appId, 'appId should be defined.');
    assert(!!microAppExtensionId, 'microAppExtensionId should be defined.');
    this.appId = appId;
    this.microAppExtensionId = microAppExtensionId;
  }
  publish(event, responseHandler) {
    assert(!!event, 'event should be defined');
    assert(!!event.type, 'event type should be defined');
    try {
      event.origin = this.appId;
      event.subscriptionId = this.subscription != null ? this.subscription.subscriptionId : null;
      if (!event.id) {
        event.id = createEventId(event.type, this.appId);
      }
      console.log('Publish request ', event);
      chrome.runtime.sendMessage(this.microAppExtensionId, event, responseHandler);
    } catch (err) {
      console.error('Unable to publish. ', err);
      throw err;
    }
  }
  bootstrap(config, publishingEvents, consumingEvents, eventCallbackHandler, bootstrapResponseHandler, registerResponseHandler) {
    try {
      console.log('About to bootstrap. ', config);
      const callBack = response => {
        if (!response || !(response !== null && response !== void 0 && response.status)) {
          console.log('Invalid Bootstrap response ', response);
          return;
        }
        if (response.status == 'FAILURE') {
          console.log('Bootstrap failed ', response);
          if (!!bootstrapResponseHandler) bootstrapResponseHandler(response);
          return;
        }
        if (response.status == 'SUCCESS') {
          this.register(publishingEvents, consumingEvents, eventCallbackHandler, registerResponseHandler);
        }
        if (!!bootstrapResponseHandler) bootstrapResponseHandler(response);
      };
      this.publish(new BootstrapRequest(config), callBack);
    } catch (err) {
      console.log('Unable to bootstrap ', err);
      throw err;
    }
  }
  shutdown(responseHandler) {
    try {
      this.publish(new ShutdownRequest(), responseHandler);
    } catch (err) {
      console.log('Unable to shutdown', err);
    }
  }
  connectNativeHost(hostAppId, responseHandler) {
    try {
      this.publish(new NativeHostConnectRequest(hostAppId), responseHandler);
    } catch (err) {
      console.log('Unable to connectNativeHost', err);
    }
  }
  disconnectNativeHost(hostAppId, responseHandler) {
    try {
      this.publish(new NativeHostDisconnectRequest(hostAppId), responseHandler);
    } catch (err) {
      console.log('Unable to disconnectNativeHost', err);
    }
  }
  register(publishingEvents, consumingEvents, eventCallbackHandler, responseHandler) {
    console.log('About to register with MicroApp Extension. App [' + this.appId + '] publishingEvents [' + publishingEvents + '] consumingEvents [' + consumingEvents + ']');
    try {
      const request = new RegistrationRequest(publishingEvents, consumingEvents);
      const callBack = response => {
        var _response$detail;
        console.log('Registration response ', response);
        if (!response || !(response !== null && response !== void 0 && response.status)) {
          console.log('Invalid Registration response ', response);
          return;
        }
        if (response.status === 'FAILURE') {
          console.log('Registration failed ', response);
          if (!!responseHandler) responseHandler(response);
          return;
        }
        if (!(response !== null && response !== void 0 && (_response$detail = response.detail) !== null && _response$detail !== void 0 && _response$detail.subscriptionId)) {
          console.log('Registration failed subscriptionId not given.', response);
          if (!!responseHandler) responseHandler(response);
          return;
        }
        this.subscription = response.detail;
        document.addEventListener('micro-app-event', function (e) {
          console.log('Event received ', e);
          if (!!(e !== null && e !== void 0 && e.detail) && !!eventCallbackHandler) {
            eventCallbackHandler(e.detail);
          }
        });
        if (!!responseHandler) responseHandler(response);
      };
      this.publish(request, callBack);
    } catch (err) {
      const message = 'Unable to connect micro-app plugin. Is it installed?. Error [' + JSON.stringify(err) + ']';
      console.error(message);
      throw message;
    }
  }
  deRegister(responseHandler) {
    let request = new DeRegistrationRequest();
    this.publish(request, responseHandler);
  }
  storePut(key, value, responseHandler) {
    assert(key != null, 'key cannot be null.');
    assert(this.subscription != null, 'Micro-App needs to register first.');
    let request = new StoragePutRequest(key, value);
    this.publish(request, responseHandler);
  }
  storeGet(key, responseHandler) {
    assert(key != null, 'key cannot be null.');
    assert(this.subscription != null, 'Micro-App needs to register first.');
    let request = new StorageGetRequest(key);
    this.publish(request, responseHandler);
  }
  enableNotifications(notificationUserDetail) {
    var _notificationUserDeta, _notificationUserDeta2;
    assert(!!notificationUserDetail, 'notificationUserDetail cannot be null.');
    assert(((_notificationUserDeta = notificationUserDetail.firstName) === null || _notificationUserDeta === void 0 ? void 0 : _notificationUserDeta.length) > 0, 'notificationUserDetail firstName should be defined.');
    assert(((_notificationUserDeta2 = notificationUserDetail.lastName) === null || _notificationUserDeta2 === void 0 ? void 0 : _notificationUserDeta2.length) > 0, 'notificationUserDetail lastName should be defined.');
    console.log('Enabling notifications');
    let request = new EnableNotificationRequest(notificationUserDetail);
    this.publish(request);
  }
  disableNotifications() {
    let request = new DisableNotificationRequest();
    this.publish(request);
  }
  route(commands, responseHandler) {
    assert(!!commands, 'commands should be defined.');
    let request = new RoutingMessage(commands);
    this.publish(request, responseHandler);
  }
  routeWithArguments(args, responseHandler) {
    assert(!!args, 'args should be defined.');
    let request = new NavigateMessage(args);
    this.publish(request, responseHandler);
  }
  subscriptions(responseHandler) {
    let request = new MicroAppEvent('SUBSCRIPTIONS');
    this.publish(request, responseHandler);
  }
  windowCapture(windowId, responseHandler) {
    let request = new WindowCaptureRequest(windowId);
    this.publish(request, responseHandler);
  }
}
class MicroAppEvent {
  constructor(type) {
    this.id = null;
    this.type = null;
    this.origin = null;
    this.subscriptionId = null;
    assert((type === null || type === void 0 ? void 0 : type.length) > 0, 'type should be defined');
    this.id = createEventId(type, origin);
    this.type = type;
    this.origin = origin;
  }
}
class BootstrapRequest extends MicroAppEvent {
  constructor(config) {
    super('BOOTSTRAP');
    this.config = null;
    this.config = config;
  }
}
class EnableNotificationRequest extends MicroAppEvent {
  constructor(notificationUserDetail) {
    super('ENABLE_NOTIFICATIONS');
    this.notificationUserDetail = null;
    this.notificationUserDetail = notificationUserDetail;
  }
}
class DisableNotificationRequest extends MicroAppEvent {
  constructor(notificationUserDetail) {
    super('DISABLE_NOTIFICATIONS');
  }
}
class ShutdownRequest extends MicroAppEvent {
  constructor() {
    super('SHUTDOWN');
  }
}
class RegistrationRequest extends MicroAppEvent {
  constructor(publishingEvents, consumingEvents) {
    super('MICRO_APP_REGISTRATION');
    this.publishingEvents = [];
    this.consumingEvents = [];
    this.publishingEvents = publishingEvents;
    this.consumingEvents = consumingEvents;
  }
}
class NativeHostConnectRequest extends MicroAppEvent {
  constructor(hostAppId) {
    super('NATIVE_HOST_CONNECT');
    this.hostAppId = null;
    assert(!!hostAppId, 'hostAppId should be defined');
    this.hostAppId = hostAppId;
  }
}
class NativeHostDisconnectRequest extends MicroAppEvent {
  constructor(hostAppId) {
    super('NATIVE_HOST_DISCONNECT');
    this.hostAppId = null;
    assert(!!hostAppId, 'hostAppId should be defined');
    this.hostAppId = hostAppId;
  }
}
class DeRegistrationRequest extends MicroAppEvent {
  constructor() {
    super('MICRO_APP_DE_REGISTRATION');
  }
}
class StoragePutRequest extends MicroAppEvent {
  constructor(key, value) {
    super('STORAGE_PUT');
    this.key = null;
    this.value = null;
    this.key = key;
    this.value = value;
  }
}
class StorageGetRequest extends MicroAppEvent {
  constructor(key) {
    super('STORAGE_GET');
    this.key = null;
    this.key = key;
  }
}
class GenericPayloadEvent extends MicroAppEvent {
  constructor(type, payload) {
    super(type);
    this.payload = void 0;
    assert(!!payload, 'payload should be defined');
    this.payload = payload;
  }
}
class RoutingMessage extends MicroAppEvent {
  constructor(commands) {
    super('ROUTE');
    this.commands = null;
    assert(commands instanceof Array, 'commands should be an array');
    this.commands = commands;
  }
}
class DirectMessage extends MicroAppEvent {
  constructor(recipientAppId) {
    super('DIRECT_MESSAGE');
    this.recipientAppId = null;
    this.recipientAppId = recipientAppId;
  }
}
class FindByUrlMessage extends MicroAppEvent {
  constructor(url) {
    super('FIND_BY_URL');
    this.url = null;
    this.url = url;
  }
}
class NavigateMessage extends MicroAppEvent {
  constructor(args) {
    super('NAVIGATE');
    this.args = null;
    this.args = args;
  }
}
class WindowCaptureRequest extends MicroAppEvent {
  constructor(windowId) {
    super('WINDOW_CAPTURE');
    this.windowId = null;
    this.windowId = windowId;
  }
}
class AssertionError {
  constructor(message) {
    this.message = null;
    this.message = message;
  }
}
function assert(condition, message) {
  if (!condition) throw new AssertionError(message || 'Assertion failed');
}
function createEventId(type, appId) {
  return 'event-' + type + '-' + appId + '-' + new Date().getTime() + '-' + Math.floor(Math.random() * Number.MAX_SAFE_INTEGER);
}
const _MicroApp = MicroApp;
const _MicroAppEvent = MicroAppEvent;
const _GenericPayloadEvent = GenericPayloadEvent;
const _RoutingMessage = RoutingMessage;
const _DirectMessage = DirectMessage;
const _FindByUrlMessage = FindByUrlMessage;

const openNewWindow = function (e, url, target, features) {
  if (features === void 0) {
    features = 'height=600,width=800';
  }
  e.preventDefault();
  window.open(url, target, features);
};

const formatCurrency = function (value, currencyCode, locale) {
  if (locale === void 0) {
    locale = 'en-US';
  }
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode
  }).format(value);
};

const scroll = function (direction, ref, id, query, offset) {
  if (offset === void 0) {
    offset = 0;
  }
  if (id && ref || id && query || ref && query) console.warn('Warning: you can only pass one of id, ref, or query.');
  if (!ref && !id && !query) console.warn('Warning: you should pass one of the following id, ref, or query.');
  let element = null;
  if (id) element = document.getElementById(id);
  if (ref) element = ref.current;
  if (query) element = document.querySelector(query);
  if (element) switch (direction) {
    case 'bottom':
      element.scrollTop = element.scrollHeight - element.clientHeight + offset;
      break;
    case 'left':
      element.scrollLeft = offset;
      break;
    case 'right':
      element.scrollLeft = element.scrollWidth - element.clientWidth + offset;
      break;
    case 'top':
    default:
      element.scrollTop = offset;
      break;
  }
};

const storage = function (type) {
  if (type === void 0) {
    type = 'sessionStorage';
  }
  const holder = type === 'localStorage' ? window.localStorage : window.sessionStorage;
  const getItem = key => {
    try {
      const item = holder.getItem(key);
      return item || null;
    } catch (error) {
      console.error(`Error: getting item from ${type}: ${error}`);
      return null;
    }
  };
  const setItem = (key, value) => {
    try {
      holder.setItem(key, value);
    } catch (error) {
      console.error(`Error: setting item in ${type}: ${error}`);
    }
  };
  const removeItem = key => {
    try {
      holder.removeItem(key);
    } catch (error) {
      console.error(`Error: removing item from ${type}: ${error}`);
    }
  };
  const clearStorage = () => {
    try {
      holder.clear();
    } catch (error) {
      console.error(`Error: clearing ${type}: ${error}`);
    }
  };
  return {
    getItem,
    setItem,
    removeItem,
    clearStorage
  };
};

const useAddressAutocomplete = () => {
  const autocompleteService = useRef(undefined);
  const autocompleteSessionToken = useRef(undefined);
  const geocoderService = useRef(undefined);
  const [input, setInput] = useState({
    input: ''
  });
  const [predictions, setPredictions] = useState({
    predictions: [],
    status: 'ZERO_RESULTS'
  });
  useEffect(() => {
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      console.warn('Warning: google script not loaded.');
      return;
    }
    autocompleteService.current = new google.maps.places.AutocompleteService();
    autocompleteSessionToken.current = new google.maps.places.AutocompleteSessionToken();
    geocoderService.current = new google.maps.Geocoder();
  }, []);
  useEffect(() => {
    if (input) if (autocompleteService.current) autocompleteService.current.getPlacePredictions({
      ...input,
      sessionToken: autocompleteSessionToken.current
    }, (autocompletePrediction, placesServiceStatus) => {
      setPredictions({
        predictions: autocompletePrediction || [],
        status: placesServiceStatus
      });
    });
  }, [input, autocompleteService]);
  const onPredictionClick = useCallback(function (placeId, callback) {
    try {
      const callbackParams = {
        address: '',
        administrativeArea: '',
        country: '',
        locality: '',
        postalCode: '',
        subLocality: '',
        longitude: 0,
        latitude: 0
      };
      if (geocoderService.current) geocoderService.current.geocode({
        placeId
      }, geocoderResult => {
        if (geocoderResult) {
          geocoderResult.map(result => result.address_components.forEach(component => {
            if (component.types.includes('street_number')) callbackParams.address = `${component.long_name} ${callbackParams.address}`;
            if (component.types.includes('route')) callbackParams.address += component.short_name;
            if (component.types.includes('locality')) callbackParams.locality = component.long_name;
            if (component.types.includes('sublocality_level_1')) callbackParams.subLocality = component.long_name;
            if (component.types.includes('administrative_area_level_1')) callbackParams.administrativeArea = component.long_name;
            if (component.types.includes('postal_code')) callbackParams.postalCode = `${component.long_name}${callbackParams.postalCode}`;
            if (component.types.includes('country')) callbackParams.country = component.long_name;
            callbackParams.latitude = result.geometry.location.lat();
            callbackParams.longitude = result.geometry.location.lng();
          }));
          callback(callbackParams);
        }
      });
      return Promise.resolve();
    } catch (e) {
      return Promise.reject(e);
    }
  }, []);
  return {
    onInputChange: setInput,
    onPredictionClick,
    predictions
  };
};

const useMicroApp = _ref => {
  let {
    appId,
    consumingEvents,
    onEvent,
    publishingEvents,
    extensionId,
    bootstrapConfig
  } = _ref;
  const [error, setError] = useState(false);
  const microAppRef = useRef(new _MicroApp(appId, extensionId));
  const onBootstrapResponse = useCallback(response => {
    console.log(`${appId} bootstrap response:`, response);
  }, [appId]);
  const onRegisterResponse = useCallback(response => {
    console.log(`${appId} registration response:`, response);
    if (response.status === 'SUCCESS') window.addEventListener('beforeunload', () => {
      console.log(`${appId} de-register from plugin.`);
      microAppRef.current.deRegister(null);
    });
  }, [appId]);
  const onPublishResponseHandler = useCallback(response => {
    console.log(`${appId} publish response:`, response);
    if (response && response.status !== 'SUCCESS') {
      console.log(`${appId} de-register from plugin.`);
      microAppRef.current.deRegister(null);
    }
  }, [appId]);
  const onPublishRoutingMessageResponse = response => {
    console.log('Successful routing message response sent back: ', JSON.stringify(response));
  };
  const publishEvent = useCallback((event, payload) => {
    const eventPublish = payload ? new _GenericPayloadEvent(event, payload) : new _MicroAppEvent(event);
    console.log(payload ? `${appId} publish ${event} with payload:${payload}` : `${appId} publish event:${event}`);
    try {
      microAppRef.current.publish(eventPublish, onPublishResponseHandler);
    } catch (err) {
      console.error(`Error: ${appId} not registered as micro-app:`, err);
      return false;
    }
    return true;
  }, [appId, onPublishResponseHandler]);
  const publishRoutingMessage = useCallback(commands => {
    const routingMessage = new _RoutingMessage(commands);
    try {
      microAppRef.current.publish(routingMessage, onPublishRoutingMessageResponse);
    } catch (err) {
      console.error('Error: unable to publish routing command to micro-app. ', routingMessage.commands);
      console.error('Error: ', JSON.stringify(err));
    }
  }, []);
  useEffect(() => {
    if (error) return;
    try {
      if (bootstrapConfig) {
        console.log(`${appId} bootstrapping as micro-app.`);
        microAppRef.current.bootstrap(bootstrapConfig, publishingEvents, consumingEvents, onEvent, onBootstrapResponse, onRegisterResponse);
      } else {
        console.log(`${appId} registered as micro-app.`);
        microAppRef.current.register(publishingEvents, consumingEvents, onEvent, onRegisterResponse);
      }
    } catch (err) {
      console.error(`Error: ${appId} unable to register as micro-app:`, err);
      setError(true);
    }
  }, [appId, error, consumingEvents, onEvent, onRegisterResponse, publishingEvents, bootstrapConfig, onBootstrapResponse]);
  return {
    error,
    publishEvent,
    onBootstrapResponse,
    onRegisterResponse,
    onPublishFailure: onPublishResponseHandler,
    publishRoutingMessage,
    onPublishRoutingMessageResponse
  };
};

const useBeforeUnload = () => {
  useEffect(() => {
    const listener = event => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', listener);
    return () => {
      window.removeEventListener('beforeunload', listener);
    };
  }, []);
};

const useClickOutside = (callback, ref, id, query) => {
  if (id && ref || id && query || ref && query) console.warn('Warning: you can only pass one of id, ref, or query.');
  if (!ref && !id && !query) console.warn('Warning: you should pass one of the following id, ref, or query.');
  useEffect(() => {
    const listener = e => {
      let element = null;
      if (id) element = document.getElementById(id);
      if (ref) element = ref.current;
      if (query) element = document.querySelector(query);
      if (element && !element.contains(e.target)) callback();
    };
    document.addEventListener('pointerdown', listener);
    return () => {
      document.removeEventListener('pointerdown', listener);
    };
  }, [callback, query, id, ref]);
};

const MicroAppContext = createContext(undefined);
const useMicroAppContext = () => useContext(MicroAppContext);

const MicroAppProvider = function (_ref) {
  let {
    appId,
    consumingEvents,
    onEvent,
    publishingEvents,
    extensionId,
    children
  } = _ref;
  const {
    error,
    publishEvent,
    publishRoutingMessage
  } = useMicroApp({
    appId,
    consumingEvents,
    onEvent,
    publishingEvents,
    extensionId
  });
  return React.createElement(MicroAppContext.Provider, {
    value: useMemo(() => ({
      error,
      publishEvent,
      publishRoutingMessage
    }), [error, publishEvent, publishRoutingMessage])
  }, children);
};
MicroAppProvider.defaultProps = {
  extensionId: undefined
};



var addressAutocomplete = {
  __proto__: null
};



var microApp = {
  __proto__: null
};



var scroll$1 = {
  __proto__: null
};



var storage$1 = {
  __proto__: null
};

export { _DirectMessage as DirectMessage, _FindByUrlMessage as FindByUrlMessage, _GenericPayloadEvent as GenericPayloadEvent, _MicroApp as MicroApp, MicroAppContext, _MicroAppEvent as MicroAppEvent, MicroAppProvider, _RoutingMessage as RoutingMessage, addressAutocomplete as addressAutocompleteModels, formatCurrency, keyEventTriggerClick, microApp as microAppModels, openNewWindow, scroll, scroll$1 as scrollModels, storage, storage$1 as storageModels, useAddressAutocomplete, useBeforeUnload, useClickOutside, useMicroApp, useMicroAppContext };
//# sourceMappingURL=index.modern.js.map
